import React, { useEffect, useMemo, useRef } from 'react';

import LocalTimeLibrary from 'local-time';

import { cn } from '../../lib/utils';

/**
 * @param {number} timestamp - The timestamp to display in seconds (not milliseconds).
 * @param {string} type - Refer to local_time options.
 * @param {string} format - Refer to local_time options.
 * @param {string} format24 - Refer to local_time options.
 * @param {number} midnightTick - The tick count of the midnight rollover, use to force a redraw of the component.
 */

const LocalTime = ({ timestamp, type = 'time', format, format24, midnightTick, className, ...props }) => {
  const timeRef = useRef(null);

  // build data attributes for local-time library
  const data = { ...props };
  if (type) data['data-local'] = type;
  if (format) data['data-format'] = format;
  if (format24) data['data-format24'] = format24;

  // create Date object from timestamp (which is in seconds)
  const date = useMemo(() => new Date(timestamp * 1000), [timestamp]);

  // Re-localize when props change. local-time formats a matching <time> node in place and stamps it
  // [data-localized]; its selector (time[data-local]:not([data-localized])) then skips that node forever.
  // When React updates this same node in place (e.g. a reused list/rail row whose timestamp changes), the
  // stale marker makes run() skip it, so it stays stuck on the raw toLocaleString() fallback. Clear the
  // marker (and the now-stale title) on our node first so run() re-localizes it with the new value.
  useEffect(() => {
    const node = timeRef.current;
    if (node) {
      node.removeAttribute('data-localized');
      node.removeAttribute('title');
    }
    LocalTimeLibrary.run();
  }, [timestamp, type, format, format24, midnightTick]);

  return (
    <time ref={timeRef} className={cn('whitespace-nowrap', className)} dateTime={date.toISOString()} {...data}>
      {date.toLocaleString()}
    </time>
  );
};

export const CalendarDate = LocalTimeLibrary.CalendarDate;

export default LocalTime;
